import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type {
  ApiResponse,
  BvSite,
  BvSiteFormData,
  BvNmiAllocation,
} from '../types/billValidationTypes';
import { auditService } from './auditService';

/**
 * Site rows carry most of their bill-validation fields inside the bv_extn JSONB
 * blob. Diffing that blob whole would produce one unreadable "bv_extn changed"
 * event, so flatten it into top-level keys before handing it to the audit log.
 */
function flattenSiteForAudit(row: Record<string, unknown>): Record<string, unknown> {
  const { bv_extn, ...rest } = row;
  return { ...rest, ...((bv_extn as Record<string, unknown>) ?? {}) };
}

// ── Server-driven list (RPC bv_sites_list / bv_sites_filter_options) ────────
export type SiteListSortField =
  | 'created_at' | 'nmi' | 'org' | 'retailer' | 'distributor'
  | 'tariff' | 'meter' | 'status' | 'enrolled';

export type SiteListParams = {
  search?: string;
  status?: string;
  state?: string;
  city?: string;
  distributor?: string;
  retailer?: string;
  tariff?: string;
  meter?: string;
  sort?: SiteListSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  size?: number;
};

export type FilterOptions = {
  state: string[];
  city: string[];
  distributor: string[];
  tariff: string[];
  meter: string[];
  retailer: string[];
};

type SiteListRow = {
  id: string;
  org_id: string;
  client_id: string;
  nmi?: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  bv_extn: BvSite['bv_extn'];
  network_codes: string[];
  network_tariff_usage: Record<string, unknown>;
  sf_site_id?: string;
  drive_link?: string;
  market_region?: string | null;
  is_embedded?: boolean | null;
  market_status?: string | null;
  created_at: string;
  client_name?: string | null;
  client_sf_account_id?: string | null;
  active_retailer?: string | null;
  total?: number;
};

function mapRow(row: SiteListRow): BvSite {
  return {
    id: row.id,
    org_id: row.org_id,
    client_id: row.client_id,
    nmi: row.nmi,
    addr_street: row.addr_street,
    addr_city: row.addr_city,
    addr_state: row.addr_state,
    addr_postcode: row.addr_postcode,
    bv_extn: row.bv_extn,
    network_codes: row.network_codes,
    network_tariff_usage: row.network_tariff_usage,
    sf_site_id: row.sf_site_id,
    drive_link: row.drive_link,
    market_region: row.market_region ?? undefined,
    is_embedded: row.is_embedded ?? null,
    market_status: row.market_status ?? null,
    created_at: row.created_at,
    client: row.client_name
      ? { id: row.client_id, name: row.client_name, sf_account_id: row.client_sf_account_id ?? undefined }
      : undefined,
    active_retailer: row.active_retailer ?? undefined,
  } as unknown as BvSite;
}

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

// Normalise NMI: strip whitespace and formatting chars only.
// Australian NMIs can be alphanumeric (e.g. QAAATESTCO1, NEM1201234)
// so letters must be preserved — only remove spaces, hyphens, underscores.
export function normaliseNmi(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Detect embedded network from 7105 prefix
export function isEmbeddedNmi(nmi: string): boolean {
  return normaliseNmi(nmi).startsWith('7105');
}

/** The AEMO identifier is 10 characters; the 11th character on retailer bills is
 *  the checksum digit. Both lengths matter here:
 *
 *  NMI_LENGTH      — the identifier itself, and what the checksum is computed
 *                    over. Distributor ranges in bv_nmi_allocation are keyed on
 *                    it, so lookups must use `nmiBase`, never the raw value.
 *  NMI_FULL_LENGTH — the full NMI as printed on a bill, checksum included. This
 *                    is what a site stores: 994 of every 1000 existing rows are
 *                    11 characters, because Salesforce and master-data imports
 *                    write them that way. The form used to strip the 11th
 *                    character, so form-created and imported sites disagreed on
 *                    format for the same column — which defeated the duplicate
 *                    check and left NMIs unmatchable against bills. */
export const NMI_LENGTH = 10;
export const NMI_FULL_LENGTH = 11;

/** The 10-character identifier, with any checksum digit dropped. Use for
 *  distributor lookups, embedded detection and duplicate comparison — never for
 *  display or storage. */
export function nmiBase(raw: string): string {
  return normaliseNmi(raw).slice(0, NMI_LENGTH);
}

const digitSum = (n: number): number =>
  String(n).split('').reduce((acc, d) => acc + Number(d), 0);

/** AEMO NMI checksum: double the ASCII value of every second character from the
 *  right, sum the decimal digits of all values, then the checksum is the digit
 *  that rounds that sum up to the next multiple of 10. */
export function nmiChecksum(nmi10: string): number | null {
  const nmi = normaliseNmi(nmi10);
  if (nmi.length !== NMI_LENGTH) return null;
  let sum = 0;
  for (let i = 0; i < NMI_LENGTH; i++) {
    const code = nmi.charCodeAt(i);
    // indices 9,7,5,3,1 — every second character counting from the right
    sum += digitSum((NMI_LENGTH - 1 - i) % 2 === 0 ? code * 2 : code);
  }
  return (10 - (sum % 10)) % 10;
}

export type NmiCheck =
  /** The full 11-character NMI as printed on a bill. `nmi` is stored verbatim.
   *  `checksumValid` is advice only — embedded and legacy NMIs do not all
   *  satisfy the AEMO checksum, so a mismatch is reported and never blocks. */
  | { kind: 'ok'; nmi: string; checksum: string; checksumValid: boolean }
  /** Only the 10-character identifier was given — the bill's checksum digit is
   *  missing. `expected` is the digit the checksum algorithm suggests, offered
   *  so the entry can be completed rather than guessed at. */
  | { kind: 'missing_checksum'; nmi: string; expected: number | null }
  | { kind: 'too_short' | 'too_long' | 'empty'; nmi: string; message: string };

/** Check a NMI's length. The full 11-character value is accepted and preserved:
 *  it is what bills print and what imports already store, so truncating it here
 *  is what previously made form-created sites disagree with imported ones. */
export function checkNmi(raw: string): NmiCheck {
  const nmi = normaliseNmi(raw);
  if (!nmi) return { kind: 'empty', nmi, message: 'NMI is required' };

  if (nmi.length === NMI_FULL_LENGTH) {
    const base = nmi.slice(0, NMI_LENGTH);
    const supplied = nmi.slice(NMI_LENGTH);
    return {
      kind: 'ok',
      nmi,
      checksum: supplied,
      checksumValid: String(nmiChecksum(base)) === supplied,
    };
  }

  if (nmi.length === NMI_LENGTH) {
    return { kind: 'missing_checksum', nmi, expected: nmiChecksum(nmi) };
  }

  return nmi.length < NMI_LENGTH
    ? { kind: 'too_short', nmi, message: `NMI must be ${NMI_FULL_LENGTH} characters as printed on the bill — got ${nmi.length}.` }
    : { kind: 'too_long', nmi, message: `NMI must be ${NMI_FULL_LENGTH} characters as printed on the bill — got ${nmi.length}.` };
}

// Look up distributor from bv_nmi_allocation for a given normalised NMI
// Uses distributor ASC ordering so 'Embedded Network' is returned before grid distributors
// when ranges overlap (e.g. TasNetworks 7101–7200 overlaps embedded 7105)
export async function lookupNmiAllocation(
  nmiNormalised: string,
): Promise<BvNmiAllocation | null> {
  const { data, error } = await supabase
    .from('bv_nmi_allocation')
    .select('*')
    .lte('nmi_from', nmiNormalised)
    .gte('nmi_to', nmiNormalised)
    .order('distributor', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('bv_nmi_allocation lookup failed:', error.message);
    return null;
  }
  return data ?? null;
}

/**
 * Translate a thrown error into a user-facing message. Surfaces the
 * `site_network_reliability_check` constraint (migration 068) as a readable
 * sentence instead of the raw PostgREST/PG constraint-violation dump.
 */
const siteErrorMessage = (error: unknown): string => {
  const msg = (error as Error).message ?? '';
  if (msg.includes('site_network_reliability_check')) {
    return 'Network reliability value must be greater than zero.';
  }
  return msg;
};

/**
 * `bv_extn.specified_capacity` from the form's flat fields. Blank, zero or not a
 * number saves as absent, which the Network Specified Capacity bucket reads as
 * "nothing to charge" rather than a capacity of 0.
 */
const specifiedCapacity = (
  value: number | '' | null | undefined,
  uom: 'kVA' | 'MVA' | '' | null | undefined,
): { value: number; uom: 'kVA' | 'MVA' } | undefined => {
  const n = value === '' || value == null ? NaN : Number(value);
  return Number.isFinite(n) && n > 0 ? { value: n, uom: uom || 'kVA' } : undefined;
};

export const siteService = {
  // Server-side filter + search + sort + pagination via RPC bv_sites_list.
  // RLS scopes by auth.uid() on the server; no org fetch here.
  list: async (params: SiteListParams = {}): Promise<ApiResponse<{ sites: BvSite[]; total: number }>> => {
    try {
      const { data, error } = await supabase.rpc('bv_sites_list', {
        p_search:   params.search      ?? null,
        p_status:   params.status      ?? null,
        p_state:    params.state       ?? null,
        p_city:     params.city        ?? null,
        p_distributor: params.distributor ?? null,
        p_retailer: params.retailer    ?? null,
        p_tariff:   params.tariff      ?? null,
        p_meter:    params.meter       ?? null,
        p_sort:     params.sort        ?? 'created_at',
        p_sort_dir: params.sortDir     ?? 'desc',
        p_page:     params.page        ?? 1,
        p_size:     params.size        ?? 50,
      });
      if (error) throw error;
      const payload = data as { total: number; rows: SiteListRow[] } | null;
      const rows  = payload?.rows  ?? [];
      const total = payload?.total ?? 0;
      return { success: true, data: { sites: rows.map(mapRow), total } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Full paginated load for export — uses the same RPC path as list() to avoid RLS issues.
  listAll: async (): Promise<ApiResponse<BvSite[]>> => {
    try {
      const all: BvSite[] = [];
      let pg = 1;
      const SIZE = 1000;
      while (true) {
        const { data, error } = await supabase.rpc('bv_sites_list', {
          p_search: null, p_status: null, p_state: null, p_city: null,
          p_distributor: null, p_retailer: null, p_tariff: null, p_meter: null,
          p_sort: 'created_at', p_sort_dir: 'desc',
          p_page: pg, p_size: SIZE,
        });
        if (error) throw error;
        const payload = data as { total: number; rows: SiteListRow[] } | null;
        const rows = payload?.rows ?? [];
        all.push(...rows.map(mapRow));
        if (rows.length < SIZE) break;
        pg++;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Distinct dropdown values for the filter bar, RLS-scoped, one call.
  listFilterOptions: async (): Promise<ApiResponse<FilterOptions>> => {
    try {
      const { data, error } = await supabase.rpc('bv_sites_filter_options');
      if (error) throw error;
      const o = (data ?? {}) as Partial<FilterOptions>;
      const strArr = (v: unknown): string[] => Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      return {
        success: true,
        data: {
          state: strArr(o.state),
          city: strArr(o.city),
          distributor: strArr(o.distributor),
          tariff: strArr(o.tariff),
          meter: strArr(o.meter),
          retailer: strArr(o.retailer),
        },
      };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // All sites for a single client (used by OrganisationDetailPage, which then
  // loads bills per site — wants the full set, not a page). RLS scopes by org.
  listByClient: async (clientId: string): Promise<ApiResponse<BvSite[]>> => {
    try {
      const { data, error } = await supabase
        .from('site')
        .select('id, org_id, client_id, nmi, addr_street, addr_city, addr_state, addr_postcode, bv_extn, network_codes, network_tariff_usage, sf_site_id, drive_link, created_at, client:client_id(id, name)')
        .eq('client_id', clientId)
        // Tender sites (empty bv_extn) are not Bill Validation sites (migration 153).
        .not('bv_extn', 'eq', '{}')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []) as unknown as BvSite[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getById: async (id: string): Promise<ApiResponse<BvSite>> => {
    try {
      const { data, error } = await supabase
        .from('site')
        .select(
          'id, org_id, client_id, nmi, addr_street, addr_city, addr_state, addr_postcode, bv_extn, network_codes, network_tariff_usage, network_tariff_dates, sf_site_id, drive_link, dma_status, loa_expiry_date, shield_platinum, meter_install_date, current_retailer, current_contract_expiry, retailer_contract_start, previous_retailer, created_at, updated_at, sf_synced_at, client:client_id(id, name), bv_dma(id, dma_provider, start_date, end_date)',
        )
        .eq('id', id)
        .single();
      if (error) throw error;
      const site = data as unknown as BvSite;

      // Resolve the contact to show on the site page. Preference order:
      // site-linked contact → client metering contact → any client contact.
      //
      // This used to be a single `role = 'metering'` + `.maybeSingle()` query,
      // which showed no contact at all whenever the site's contact had another
      // role, and errored out (also showing nothing) when a client had more
      // than one metering contact.
      if (site.client_id) {
        const contactQuery = () =>
          supabase.from('contact').select('id, first_name, last_name, email, role');

        const candidates = [
          contactQuery().eq('site_id', id),
          contactQuery().eq('client_id', site.client_id).eq('role', 'metering'),
          contactQuery().eq('client_id', site.client_id).order('created_at'),
        ];

        for (const query of candidates) {
          const { data, error } = await query.limit(1);
          if (error) console.error('[siteService.getById] contact fetch error:', error);
          if (data?.[0]) { site.metering_contact = data[0]; break; }
        }
      }

      return { success: true, data: site };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (form: BvSiteFormData): Promise<ApiResponse<BvSite>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const {
        nmi, client_id, sf_site_id,
        addr_street, addr_city, addr_state, addr_postcode,
        is_embedded: formEmbedded,
        ...bvFields
      } = form;

      // Stored verbatim, checksum digit included — see NMI_FULL_LENGTH.
      const nmiFull = normaliseNmi(nmi);
      const base = nmiBase(nmi);

      // There is no unique constraint on site.nmi, so the same NMI could be
      // added twice and then appear as two entries for the one meter.
      //
      // This compared the normalised value with `eq`, which silently stopped
      // working once the two writers disagreed on format: the form supplied 10
      // characters while imports had written 11, so an existing row was never
      // matched and the duplicate was created anyway. Production currently holds
      // 195 exact duplicate NMIs as a result. Matching on the 10-character base
      // as a prefix catches both formats, and also catches two rows that differ
      // only in the checksum digit — which is the same meter either way.
      const { data: clash, error: clashErr } = await supabase
        .from('site')
        .select('id, nmi')
        .eq('org_id', orgId)
        .like('nmi', `${base}%`)
        .limit(1);
      if (clashErr) throw clashErr;
      if (clash?.length) {
        return { success: false, error: `NMI ${clash[0].nmi} already exists on another site.` };
      }

      // Always derive is_embedded from NMI — form field is informational only
      const embedded = isEmbeddedNmi(nmi);
      // Distributor ranges are keyed on the 10-character identifier.
      const alloc = await lookupNmiAllocation(base);

      const bv_extn = {
        // The 10-character identifier, for matching against bills and ranges.
        nmi_normalised: base,
        is_embedded: embedded,
        state: bvFields.state ?? alloc?.state ?? addr_state,
        network_distributor: bvFields.network_distributor ?? alloc?.distributor,
        network_tariff_code: bvFields.network_tariff_code,
        tni: bvFields.tni,
        dlf_code: bvFields.dlf_code,
        mlf_code: bvFields.mlf_code,
        dlf_override: bvFields.dlf_override !== '' ? bvFields.dlf_override : undefined,
        mlf_override: bvFields.mlf_override !== '' ? bvFields.mlf_override : undefined,
        network_tariff_start: bvFields.network_tariff_start,
        market_status: bvFields.market_status,
        market_region: bvFields.market_region,
        customer_classes: bvFields.customer_classes
          ? bvFields.customer_classes.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        bill_val_status: bvFields.bill_val_status ?? 'Not Enrolled',
        enrolment_date: bvFields.enrolment_date ?? null,
        unenrolment_date: bvFields.unenrolment_date ?? null,
        network_reliability:
          bvFields.network_reliability_value !== '' && bvFields.network_reliability_value != null
            ? { value: Number(bvFields.network_reliability_value), uom: bvFields.network_reliability_uom || 'kW' }
            : undefined,
        specified_capacity: specifiedCapacity(bvFields.specified_capacity_value, bvFields.specified_capacity_uom),
        cpd_demand: specifiedCapacity(bvFields.cpd_demand_value, bvFields.cpd_demand_uom),
      };

      const { data, error } = await supabase
        .from('site')
        .insert({
          org_id: orgId,
          client_id,
          nmi: nmiFull,
          // A site entered through Bill Validation is a Shield Platinum site by
          // definition (job 10, confirmed with the customer). The column is
          // otherwise only ever written by the Salesforce sync, so a manually
          // created site used to sit at NULL and show no Shield Platinum badge.
          shield_platinum: true,
          sf_site_id: sf_site_id || null,
          addr_street,
          addr_city,
          addr_state,
          addr_postcode,
          bv_extn,
        })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as unknown as BvSite };
    } catch (error: unknown) {
      return { success: false, error: siteErrorMessage(error) };
    }
  },

  update: async (id: string, form: Partial<BvSiteFormData>): Promise<ApiResponse<BvSite>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('site')
        .select('org_id, bv_extn, nmi, addr_street, addr_city, addr_state, addr_postcode')
        .eq('id', id)
        .single();
      if (fetchErr) throw fetchErr;

      // A tender site has an empty bv_extn and is hidden from the BV list
      // (migration 153). Saving the BV form on one — reachable through a bill
      // that was assigned to it — populated bv_extn, so it reappeared on the
      // list next to the real BV site for the same NMI. Refuse when that real
      // site exists; a tender-only NMI can still be enrolled this way.
      const existingExtn = (existing as { bv_extn?: Record<string, unknown> | null }).bv_extn;
      const existingNmi = (existing as { nmi?: string | null }).nmi;
      if ((!existingExtn || Object.keys(existingExtn).length === 0) && existingNmi) {
        const { data: bvTwin, error: twinErr } = await supabase
          .from('site')
          .select('id, nmi')
          .eq('org_id', (existing as { org_id: string }).org_id)
          .like('nmi', `${nmiBase(existingNmi)}%`)
          .neq('id', id)
          .not('bv_extn', 'is', null)
          .neq('bv_extn', '{}')
          .limit(1);
        if (twinErr) throw twinErr;
        if (bvTwin?.length) {
          return {
            success: false,
            error: `This is a tender site. NMI ${bvTwin[0].nmi} is already a Bill Validation site — edit that site instead.`,
          };
        }
      }

      const {
        nmi, sf_site_id,
        addr_street, addr_city, addr_state, addr_postcode,
        state, network_distributor, network_tariff_code,
        tni, dlf_code, mlf_code, mlf_override, dlf_override,
        network_tariff_start,
        network_distributor_start_date, network_distributor_end_date,
        market_status, market_region, customer_classes,
        is_embedded: _is_embedded,
        bill_val_status, enrolment_date, unenrolment_date,
        metering_provider, retail_tou_schedule,
        network_reliability_value, network_reliability_uom,
        specified_capacity_value, specified_capacity_uom,
        cpd_demand_value, cpd_demand_uom,
      } = form;

      const bv_extn = {
        ...(existing?.bv_extn ?? {}),
        ...(nmi !== undefined ? { nmi_normalised: normaliseNmi(nmi), is_embedded: isEmbeddedNmi(nmi) } : {}),
        ...(state !== undefined ? { state } : {}),
        ...(network_distributor !== undefined ? { network_distributor } : {}),
        ...(network_tariff_code !== undefined ? { network_tariff_code } : {}),
        ...(tni !== undefined ? { tni } : {}),
        ...(dlf_code !== undefined ? { dlf_code } : {}),
        ...(mlf_code !== undefined ? { mlf_code } : {}),
        // '' clears the override and hands the site back to the table.
        ...(mlf_override !== undefined ? { mlf_override: mlf_override !== '' ? mlf_override : null } : {}),
        ...(dlf_override !== undefined ? { dlf_override: dlf_override !== '' ? dlf_override : null } : {}),
        ...(network_tariff_start !== undefined ? { network_tariff_start } : {}),
        // Empty string clears rather than storing '' — a blank end date means
        // "still with this distributor", and '' would read as a set-but-empty date.
        ...(network_distributor_start_date !== undefined
          ? { network_distributor_start_date: network_distributor_start_date || undefined } : {}),
        ...(network_distributor_end_date !== undefined
          ? { network_distributor_end_date: network_distributor_end_date || undefined } : {}),
        ...(market_status !== undefined ? { market_status } : {}),
        ...(market_region !== undefined ? { market_region } : {}),
        ...(customer_classes !== undefined ? {
          customer_classes: customer_classes.split(',').map((s) => s.trim()).filter(Boolean),
        } : {}),
        ...(bill_val_status !== undefined ? { bill_val_status } : {}),
        ...(enrolment_date !== undefined ? { enrolment_date } : {}),
        ...(unenrolment_date !== undefined ? { unenrolment_date } : {}),
        ...(metering_provider !== undefined ? { metering_provider: metering_provider || undefined } : {}),
        ...(retail_tou_schedule !== undefined ? { retail_tou_schedule: retail_tou_schedule || undefined } : {}),
        ...(network_reliability_value !== undefined ? {
          network_reliability:
            network_reliability_value !== '' && network_reliability_value != null
              ? { value: Number(network_reliability_value), uom: network_reliability_uom || 'kW' }
              : undefined,
        } : {}),
        ...(specified_capacity_value !== undefined
          ? { specified_capacity: specifiedCapacity(specified_capacity_value, specified_capacity_uom) } : {}),
        ...(cpd_demand_value !== undefined
          ? { cpd_demand: specifiedCapacity(cpd_demand_value, cpd_demand_uom) } : {}),
      };

      const patch: Record<string, unknown> = { bv_extn };
      if (nmi) {
        const nmiFull = normaliseNmi(nmi);
        if (nmiFull !== normaliseNmi((existing as { nmi?: string }).nmi ?? '')) {
          // Same duplicate guard as create, and it had the same format-mismatch
          // flaw — compare on the 10-character base so both stored formats and a
          // checksum-only difference are caught.
          const { data: clash, error: clashErr } = await supabase
            .from('site')
            .select('id, nmi')
            .like('nmi', `${nmiBase(nmi)}%`)
            .neq('id', id)
            .limit(1);
          if (clashErr) throw clashErr;
          if (clash?.length) {
            return { success: false, error: `NMI ${clash[0].nmi} already exists on another site.` };
          }
        }
        patch.nmi = nmiFull;
      }
      if (sf_site_id !== undefined) patch.sf_site_id = sf_site_id || null;
      if (addr_street !== undefined) patch.addr_street = addr_street;
      if (addr_city !== undefined) patch.addr_city = addr_city;
      if (addr_state !== undefined) patch.addr_state = addr_state;
      if (addr_postcode !== undefined) patch.addr_postcode = addr_postcode;

      const { data, error } = await supabase
        .from('site')
        .update(patch)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Audit (migration 066). Flattening bv_extn means the history shows
      // "MLF value 0.9931 → 1.0042" rather than a wall of JSON. Fire-and-forget:
      // a failed audit write must never fail the user's edit.
      void auditService.logFieldChanges(
        { entity_type: 'site', entity_id: id, site_id: id },
        flattenSiteForAudit(existing as Record<string, unknown>),
        flattenSiteForAudit({ ...(existing as Record<string, unknown>), ...patch }),
      );

      return { success: true, data: data as unknown as BvSite };
    } catch (error: unknown) {
      return { success: false, error: siteErrorMessage(error) };
    }
  },

  /** Rows that reference a site and would be orphaned by deleting it. Used to
   *  explain a refusal rather than letting a foreign-key error surface raw. */
  countDependents: async (id: string): Promise<ApiResponse<{ bills: number; contracts: number; contacts: number }>> => {
    try {
      const counted = async (table: string, column = 'site_id') => {
        const { count, error } = await supabase
          .from(table)
          .select('id', { count: 'exact', head: true })
          .eq(column, id);
        if (error) throw error;
        return count ?? 0;
      };
      const [bills, contracts, contacts] = await Promise.all([
        counted('bv_bill'),
        counted('bv_contract'),
        counted('contact'),
      ]);
      return { success: true, data: { bills, contracts, contacts } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Delete a site.
   *
   *  Refuses while bills exist. Note this is the ONLY thing protecting them:
   *  bv_bill.site_id is ON DELETE CASCADE, so the database would drop the bills
   *  without complaint. The reported need was removing test NMIs, which have no
   *  bills.
   *
   *  Everything else referencing the site is handled by its FK action —
   *  contracts, rates, NEM data and contacts CASCADE (contact.site_id is NOT
   *  NULL since migration 062), attachments and inbox emails SET NULL. No
   *  manual cleanup here: deleting contacts by hand would fail against the
   *  NOT NULL column and is unnecessary anyway. */
  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const deps = await siteService.countDependents(id);
      if (!deps.success || !deps.data) return { success: false, error: deps.error ?? 'Dependency check failed' };
      if (deps.data.bills > 0) {
        return {
          success: false,
          error: `This site has ${deps.data.bills} bill${deps.data.bills === 1 ? '' : 's'} attached. `
            + 'Delete the bills first, or set the site to Cancelled instead of deleting it.',
        };
      }

      // `.select()` so the caller can tell a real delete from an RLS no-op:
      // without a DELETE policy Postgres removes nothing and still reports success.
      const { data, error } = await supabase.from('site').delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data?.length) {
        return { success: false, error: 'Site was not deleted — you may not have permission to delete sites.' };
      }
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Distinct distributor names from the NMI allocation reference table, for the
   *  site form's Distributor picker. Free text let typos through, which then
   *  failed to match tariff codes via distributorsMatch. */
  listDistributors: async (): Promise<ApiResponse<string[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_nmi_allocation')
        .select('distributor')
        .order('distributor');
      if (error) throw error;
      const names = [...new Set((data ?? [])
        .map((r) => (r as { distributor?: string | null }).distributor?.trim())
        .filter((n): n is string => !!n))];
      return { success: true, data: names.sort((a, b) => a.localeCompare(b)) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

export { effectiveSiteStatus } from '../lib/siteStatus';
